import useSwr from 'swr';
import Axios from '@/utils/Axios';
import getDomainPlatform, {
  DomainPlatformValue,
} from '@/utils/getDomainPlatform';
import { getSubject } from '@/utils/subject';

interface RequestQuery {
  platform?: DomainPlatformValue;
  /**
   * 用户当前学科；FS 平台不传时返回账号当前所在学科的教材档案
   */
  subject?: number;
}

export interface Profile {
  /**
   * 地区代码
   */
  areaCode?: string;
  /**
   * 头像
   */
  avatar?: string;
  /**
   * 是否启用
   */
  enabled: number;
  /**
   * 性别 1男 2女
   */
  gender?: number;
  /**
   * 用户id
   */
  id: number;
  /**
   * 身份 'staff' | 'student' | 'teacher' | 'parent'
   */
  identity: Identity;
  /**
   * 仅 FS 平台不传 subject 时返回：教材档案所属学科是否已标记为账号当前所在的学科；
   * false 表示这条档案是按「账号只有一条档案」取到的，还没有标记
   */
  isSubjectCurrent?: boolean;
  /**
   * 姓名
   */
  name: string;
  /**
   * 手机号
   */
  phone?: string;
  /**
   * 偏好是否已设置
   */
  prefer: boolean;
  /**
   * 系统角色
   */
  sysRole: string;
  /**
   * 用户设置课
   */
  section?: number;
  /**
   * 教材档案所属学科：传 subject 时等于入参；FS 平台不传时为账号当前所在学科
   */
  subject?: number;
  /**
   * 用户设置单元
   */
  unit?: number;
  /**
   * 教师工号
   */
  teacherCode?: string;
}

export enum Identity {
  Parent = 'parent',
  Staff = 'staff',
  Student = 'student',
  Teacher = 'teacher',
}

export async function getProfile(
  options: {
    /**
     * 学科；不传时使用站点学科（getSubject()）。FS 平台没有站点学科，
     * 不传即由服务端返回账号当前所在学科的教材档案
     */
    subject?: number;
  } = {},
) {
  const platform = getDomainPlatform();

  const subject =
    options.subject ??
    (platform === DomainPlatformValue.Fs ? undefined : Number(getSubject().id));

  const response = await Axios.get<Profile>(`/users/profile`, {
    params: {
      platform,
      subject,
    } satisfies RequestQuery,
  });
  return response.data;
}

export default function useProfile() {
  const platform = getDomainPlatform();

  const { data, mutate } = useSwr(['/users/profile', { platform }], () =>
    getProfile(),
  );

  return {
    data,
    mutate,
  } as const;
}
